import { useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import {
  Zap, History, LayoutGrid, BarChart3, UtensilsCrossed,
  Tags, Users, Settings as SettingsIcon, LogOut, Store,
} from "lucide-react";

const NAV = [
  { to: "/pos", label: "Billing", icon: Zap, shortcut: "" },
  { to: "/history", label: "Bill History", icon: History },
  { to: "/tables", label: "Tables", icon: LayoutGrid },
  { to: "/dashboard", label: "Dashboard", icon: BarChart3, admin: true },
  { to: "/menu", label: "Menu", icon: UtensilsCrossed, admin: true },
  { to: "/categories", label: "Categories", icon: Tags, admin: true },
  { to: "/cashiers", label: "Cashiers", icon: Users, admin: true },
  { to: "/settings", label: "Settings", icon: SettingsIcon, admin: true },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { settings, loadSettings, loadMenu } = useData();
  const navigate = useNavigate();

  useEffect(() => {
    loadSettings();
    loadMenu();
  }, [loadSettings, loadMenu]);

  const onLogout = () => {
    logout();
    navigate("/login");
  };

  const items = NAV.filter((n) => !n.admin || user?.role === "admin");

  return (
    <div className="min-h-screen flex bg-[#0B0F17] text-[#F3F4F6]">
      <aside className="w-[232px] shrink-0 bg-[#111827] border-r border-[#23334D] flex flex-col h-screen sticky top-0">
        <div className="px-5 py-5 border-b border-[#23334D] flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#10B981] flex items-center justify-center shrink-0">
            <Store className="w-5 h-5 text-[#06221a]" />
          </div>
          <div className="min-w-0">
            <div className="font-head font-bold text-[15px] leading-tight truncate">
              {settings?.restaurant_name || "Restaurant POS"}
            </div>
            <div className="text-[11px] text-[#6B7280] uppercase tracking-wide">POS & Billing</div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-1">
          {items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.to.slice(1)}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-[#10B981] text-[#06221a]"
                    : "text-[#9CA3AF] hover:bg-[#1E2C45] hover:text-white"
                }`
              }
            >
              <n.icon className="w-[18px] h-[18px]" />
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-[#23334D] p-3">
          <div className="px-2 py-2 mb-1">
            <div className="text-sm font-semibold truncate">{user?.name}</div>
            <div className="text-[11px] text-[#6B7280] uppercase tracking-wide">{user?.role}</div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={onLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-[#9CA3AF] hover:bg-[#1E2C45] hover:text-[#EF4444] transition-colors"
          >
            <LogOut className="w-[18px] h-[18px]" /> Logout
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 h-screen overflow-y-auto">{children}</main>
    </div>
  );
}
